import { useNavigate, useLocation } from "@tanstack/react-router";
import { LogOut, X, GripVertical, Sliders } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useAuth } from "@/lib/auth";
import { useEffectiveNav } from "@/hooks/useEffectiveNav";
import { useIsMobile } from "@/hooks/use-mobile";
import { openNavCustomizer } from "@/lib/nav-customizer";
import { buzz } from "@/lib/dar-prefs";
import { roleLabel } from "@/lib/roles";
import { moveItem } from "@/lib/nav-prefs";
import type { NavItem } from "@/lib/nav-catalog";
import { Logo } from "./Logo";
import { cn } from "@/lib/utils";

function SortableRow({
  item,
  active,
  onSelect,
}: {
  item: NavItem;
  active: boolean;
  onSelect: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });
  const Icon = item.icon;
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cn(isDragging && "z-10 scale-[1.02] shadow-2xl shadow-black/60")}
    >
      <div
        className={cn(
          "group flex w-full items-center gap-3 rounded-md border-l-2 pr-2 text-sm transition-colors",
          active
            ? "border-[#CC0000] bg-[#CC0000]/10 text-[#CC0000]"
            : "border-transparent text-[#999999] hover:bg-white/5 hover:text-white",
          isDragging && "bg-white/10 text-white",
        )}
      >
        <button
          type="button"
          onClick={onSelect}
          className="flex min-w-0 flex-1 items-center gap-3 px-3 py-3 text-left"
        >
          <Icon
            className={cn(
              "h-5 w-5 shrink-0",
              active ? "text-[#CC0000]" : "text-[#999999] group-hover:text-white",
            )}
          />
          <span className="min-w-0 flex-1 truncate">{item.label}</span>
        </button>
        <span
          {...attributes}
          {...listeners}
          role="button"
          tabIndex={-1}
          aria-label={`Reorder ${item.label}`}
          className="shrink-0 touch-none cursor-grab p-2 text-white/25"
        >
          <GripVertical className="h-4 w-4" aria-hidden />
        </span>
      </div>
    </li>
  );
}

export function SideMenu({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, currentRole, isPlatformAdmin, signOut } = useAuth();
  const nav = useEffectiveNav();

  // Lock body scroll while open + close on Esc + trap focus inside the drawer,
  // restoring focus to the trigger on close (WCAG 2.1.2 / 2.4.3).
  const panelRef = useRef<HTMLElement | null>(null);
  const restoreFocusTo = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!open) return;
    restoreFocusTo.current = (document.activeElement as HTMLElement) ?? null;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusables = () =>
      Array.from(
        panelRef.current?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      ).filter((el) => el.offsetParent !== null || el === document.activeElement);
    const first = focusables()[0];
    first?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const items = focusables();
      if (items.length === 0) return;
      const firstEl = items[0];
      const lastEl = items[items.length - 1];
      const activeEl = document.activeElement as HTMLElement | null;
      if (e.shiftKey && (activeEl === firstEl || !panelRef.current?.contains(activeEl))) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && activeEl === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
      restoreFocusTo.current?.focus?.();
    };
  }, [open, onClose]);


  const go = (to: string) => {
    onClose();
    navigate({ to: to as never });
  };

  const handleSignOut = async () => {
    onClose();
    await signOut();
    navigate({ to: "/" });
  };

  // Long-press to drag: 280ms hold with small tolerance, matching the DAR orb feel.
  const sensors = useSensors(
    useSensor(TouchSensor, { activationConstraint: { delay: 280, tolerance: 8 } }),
    useSensor(PointerSensor, { activationConstraint: { delay: 280, tolerance: 8 } }),
  );

  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const ids = nav.sidebarIds;
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    buzz(12);
    void nav.save({ sidebar_order: moveItem(ids, from, to) });
  };

  const displayName =
    (user?.user_metadata as { full_name?: string; name?: string } | undefined)?.full_name ??
    (user?.user_metadata as { name?: string } | undefined)?.name ??
    user?.email?.split("@")[0] ??
    "Guest";
  const initials = (displayName || "U").slice(0, 2).toUpperCase();
  const roleLabelText = isPlatformAdmin ? "Platform Owner" : roleLabel(currentRole);

  // The drawer is the access point for pages that are NOT pinned to the mobile
  // primary nav, so a pinned page never appears twice.
  const isMobile = useIsMobile();
  const menuItems: NavItem[] = isMobile
    ? nav.sidebarItems.filter((i) => !nav.bottomIds.includes(i.id))
    : nav.sidebarItems;

  const isActive = (to: string) =>
    location.pathname === to || location.pathname.startsWith(to + "/");

  // Swipe-to-close
  const [dragX, setDragX] = useState(0);
  const dragging = useRef(false);
  const startX = useRef(0);
  const startY = useRef(0);
  const decided = useRef<null | "h" | "v">(null);

  const onTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length !== 1) return;
    dragging.current = true;
    decided.current = null;
    startX.current = e.touches[0].clientX;
    startY.current = e.touches[0].clientY;
    setDragX(0);
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (!dragging.current) return;
    const dx = e.touches[0].clientX - startX.current;
    const dy = e.touches[0].clientY - startY.current;
    if (decided.current === null) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      decided.current = Math.abs(dx) > Math.abs(dy) ? "h" : "v";
    }
    if (decided.current !== "h") return;
    setDragX(Math.min(0, dx));
  };
  const onTouchEnd = () => {
    if (!dragging.current) return;
    dragging.current = false;
    const shouldClose = dragX < -80;
    setDragX(0);
    decided.current = null;
    if (shouldClose) onClose();
  };

  useEffect(() => {
    if (!open) {
      setDragX(0);
      dragging.current = false;
      decided.current = null;
    }
  }, [open]);

  return (
    <>
      {/* Backdrop */}
      <div
        aria-hidden={!open}
        onClick={onClose}
        onTouchStart={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className={cn(
          "fixed inset-0 z-(--z-drawer) bg-black/60 backdrop-blur-sm transition-opacity duration-200",
          open ? "opacity-100" : "pointer-events-none opacity-0",
        )}
      />
      {/* Drawer */}
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Main menu"

        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
        className={cn(
          "fixed top-0 left-0 z-(--z-drawer-panel) flex h-[100dvh] max-h-[100dvh] w-[86%] max-w-[320px] flex-col overflow-hidden bg-[#0D0D0D] text-white shadow-2xl",
          dragging.current && dragX !== 0
            ? "transition-none"
            : "transition-transform duration-300 ease-out",
          "will-change-transform touch-pan-y",
          open ? "translate-x-0" : "-translate-x-full",
        )}
        style={{
          paddingTop: "env(safe-area-inset-top)",
          paddingBottom: "env(safe-area-inset-bottom)",
          transform: open && dragX < 0 ? `translateX(${dragX}px)` : undefined,
        }}
      >
        {/* Header */}
        <div className="flex shrink-0 items-start gap-3 border-b border-white/10 px-4 py-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#CC0000]/20 text-sm font-bold text-[#CC0000]">
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <Logo />
            </div>
            <div className="mt-1 truncate text-sm font-semibold text-white">{displayName}</div>
            <div className="truncate text-[11px] uppercase tracking-wide text-[#C0C0C0]">
              {roleLabelText}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            className="rounded-md p-1.5 text-white/80 hover:bg-white/10 hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Menu */}
        <nav
          className="min-h-0 flex-1 touch-pan-y overflow-y-auto overflow-x-hidden overscroll-contain px-2 py-2 [-webkit-overflow-scrolling:touch]"
          style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 2rem)" }}
          aria-label="Sidebar"
        >
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis]}
            onDragStart={() => buzz(18)}
            onDragEnd={onDragEnd}
          >
            <SortableContext
              items={menuItems.map((i) => i.id)}
              strategy={verticalListSortingStrategy}
            >
              <ul className="space-y-0.5">
                {menuItems.map((item) => (
                  <SortableRow
                    key={item.id}
                    item={item}
                    active={isActive(item.route)}
                    onSelect={() => go(item.route)}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>

          <div className="my-3 h-px bg-white/10" />

          <button
            type="button"
            onClick={() => {
              onClose();
              openNavCustomizer();
            }}
            className="flex w-full items-center gap-3 rounded-md border-l-2 border-transparent px-3 py-3 text-sm text-[#999999] hover:bg-white/5 hover:text-white"
          >
            <Sliders className="h-5 w-5" />
            <span>Customize navigation</span>
          </button>

          <button
            type="button"
            onClick={handleSignOut}
            className="flex w-full items-center gap-3 rounded-md border-l-2 border-transparent px-3 py-3 text-sm text-[#999999] hover:bg-white/5 hover:text-white"
          >
            <LogOut className="h-5 w-5" />
            <span>Sign Out</span>
          </button>
        </nav>
      </aside>
    </>
  );
}
